<template>
  <div class="border border-slate-200 bg-white p-4 shadow-sm">
    <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h3 class="text-base font-semibold text-slate-800">Histórico de correntes</h3>
      <div class="flex flex-wrap items-center gap-2">
        <label class="flex items-center gap-1.5 text-sm text-slate-600">
          <span>Alarme:</span>
          <select
            v-model="selectedAlarmType"
            class="border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
            <option
              v-for="opt in alarmTypeOptions"
              :key="opt.value"
              :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </label>
        <TimeRangeFilter default-preset="24h" @change="onRangeChange" />
        <button
          type="button"
          class="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-400"
          title="Maximizar gráfico"
          @click="openFullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
          Maximizar
        </button>
      </div>
    </div>

    <SensorChartAlarmCards
      v-model:expanded="alarmsSectionExpanded"
      :alarms="alarmList"
      :is-loading="isAlarmsLoading"
      accent="red"
      @select-alarm="openAlarmDetail"
      @hover-alarm="onAlarmHover" />

    <div ref="chartContainer" class="h-96 w-full min-h-[24rem]" />
    <p v-if="isLoading && !hasAnySeriesData" class="mt-2 text-center text-sm text-slate-500">
      Carregando...
    </p>
    <p v-else-if="!isLoading && !hasAnySeriesData" class="mt-2 text-center text-sm text-slate-500">
      Nenhum dado no período selecionado.
    </p>
  </div>

  <!-- Overlay tela cheia: mesmos filtros + gráfico -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isFullscreen"
        class="fixed inset-0 z-[100000] flex flex-col bg-white"
        role="dialog"
        aria-modal="true"
        aria-label="Gráfico de correntes em tela cheia">
        <div class="flex flex-none flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <h3 class="text-lg font-semibold text-slate-800">Histórico de correntes</h3>
          <div class="flex flex-wrap items-center gap-2">
            <label class="flex items-center gap-1.5 text-sm text-slate-600">
              <span>Alarme:</span>
              <select
                v-model="selectedAlarmType"
                class="border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                <option
                  v-for="opt in alarmTypeOptions"
                  :key="opt.value"
                  :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </label>
            <TimeRangeFilter
              :initial-range="currentRange"
              default-preset="24h"
              @change="onRangeChange" />
            <button
              type="button"
              class="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700"
              title="Sair da tela cheia"
              @click="closeFullscreen">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Fechar
            </button>
          </div>
        </div>
        <div class="flex flex-1 flex-col overflow-auto p-4">
          <SensorChartAlarmCards
            v-model:expanded="alarmsSectionExpanded"
            :alarms="alarmList"
            :is-loading="isAlarmsLoading"
            accent="red"
            @select-alarm="openAlarmDetail"
            @hover-alarm="onAlarmHover" />
          <div ref="chartContainerFullscreen" class="min-h-[28rem] flex-1 w-full" />
          <p v-if="isLoading && !hasAnySeriesData" class="mt-2 text-center text-sm text-slate-500">
            Carregando...
          </p>
          <p v-else-if="!isLoading && !hasAnySeriesData" class="mt-2 text-center text-sm text-slate-500">
            Nenhum dado no período selecionado.
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import SensorResource from '@/api/resources/sensor'
import SensorChartAlarmCards from '@/components/Sensors/SensorChartAlarmCards.vue'
import type { TimeRangeValue } from '@/components/Globals/TimeRangeFilter'
import { TimeRangeFilter } from '@/components/Globals/TimeRangeFilter'
import type { ISensorChartOverlayAlarm } from '@/interfaces/components/sensorChartAlarm'
import {
  alarmsToPeriodsMs,
  fetchSensorChartOverlayAlarms,
  filterChartOverlayAlarms,
  resolveChartHoverIndex,
} from '@/utils/sensor-chart-alarms'
import { buildHoveredAlarmMarkArea } from '@/utils/components/alarm-detail'
import { buildCurrentChartOption } from '@/utils/components/alarm-detail/current-chart-options'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { normalizeEnergyReadingsForChart } from '@/utils/energy-readings-normalize'

/** Tipos de alarme relevantes para sensor de energia (valor enviado à API) */
const ENERGY_ALARM_TYPES = [
  { value: 'interruption', label: 'Interrupção' },
  { value: 'low_load', label: 'Carga baixa' },
  { value: 'power_factor', label: 'Fator de potência' },
  { value: 'activation_peak_hour', label: 'Pico de ativação' },
  { value: 'empty_mixer', label: 'Misturador vazio' },
  { value: 'outside_regular_time', label: 'Fora do horário' },
  { value: 'burnt_resistor', label: 'Resistor queimado' },
  { value: 'defrost_time', label: 'Tempo de degelo' },
  { value: 'no_data', label: 'Sem dados' },
  { value: 'overvoltage', label: 'Sobretensão' },
] as const

const alarmTypeOptions = ENERGY_ALARM_TYPES.map((o) => ({ value: o.value, label: o.label }))

const router = useRouter()

const props = defineProps<{
  sensorId: number
}>()

const chartContainer = ref<HTMLDivElement | null>(null)
const chartContainerFullscreen = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const isFullscreen = ref(false)
/** Seção "Alarmes encontrados" expandida (true) ou recolhida (false) */
const alarmsSectionExpanded = ref(true)
/** Índice do card de alarme sob o mouse (para destacar a faixa no gráfico) */
const hoveredAlarmIndex = ref<number | null>(null)

const isLoading = ref(false)
const isAlarmsLoading = ref(false)
const selectedAlarmType = ref<string>(ENERGY_ALARM_TYPES[0].value)

/** Séries: corrente A, B, C e média. Cada uma é [timestamp_ms, value][] */
const seriesCurrentA = ref<[number, number][]>([])
const seriesCurrentB = ref<[number, number][]>([])
const seriesCurrentC = ref<[number, number][]>([])
const seriesAverage = ref<[number, number][]>([])

const alarmPeriods = ref<[number, number][]>([])
/** Estado do zoom do gráfico (preservado ao maximizar/minimizar) */
const savedDataZoom = ref({ start: 0, end: 100 })
const alarmList = ref<ISensorChartOverlayAlarm[]>([])

const sensorAlarms = computed(() => filterChartOverlayAlarms(alarmList.value))

const hasAnySeriesData = computed(
  () =>
    seriesCurrentA.value.length > 0 ||
    seriesCurrentB.value.length > 0 ||
    seriesCurrentC.value.length > 0 ||
    seriesAverage.value.length > 0,
)

/** window_period para a API: 5m ou 15m conforme o intervalo */
function windowPeriodMinutes(range: TimeRangeValue): string {
  const hours = (range.end.getTime() - range.start.getTime()) / (60 * 60 * 1000)
  return hours > 48 ? '15m' : '5m'
}

async function fetchAlarmPeriods(range: TimeRangeValue) {
  if (!props.sensorId || !selectedAlarmType.value) return
  isAlarmsLoading.value = true
  try {
    const list = await fetchSensorChartOverlayAlarms({
      sensorId: props.sensorId,
      sensorAlarmType: selectedAlarmType.value,
      startDate: range.start.toISOString(),
      endDate: range.end.toISOString(),
    })
    alarmList.value = list
    const sensorOnly = filterChartOverlayAlarms(list)
    alarmPeriods.value = alarmsToPeriodsMs(sensorOnly, range.end.getTime())
  } catch {
    alarmList.value = []
    alarmPeriods.value = []
  } finally {
    isAlarmsLoading.value = false
  }
}

function onAlarmHover(alarm: ISensorChartOverlayAlarm | null) {
  hoveredAlarmIndex.value = resolveChartHoverIndex(alarm, sensorAlarms.value)
}

function openAlarmDetail(alarm: ISensorChartOverlayAlarm) {
  if (alarm.origin === 'group') {
    if (!alarm.group?.id) return
    router.push({
      name: 'group-alarm-detail',
      params: { groupId: String(alarm.group.id), alarmId: String(alarm.id) },
      query: { alarmType: alarm.alarm_type ?? '' },
    })
    return
  }

  router.push({
    name: 'alarm-detail',
    params: {
      sensorId: String(props.sensorId),
      alarmId: String(alarm.id),
    },
    query: { alarmType: alarm.alarm_type ?? '' },
  })
}

async function fetchReadings(range: TimeRangeValue) {
  if (!props.sensorId) return
  isLoading.value = true
  try {
    const dateRange = `start_date=${encodeURIComponent(range.start.toISOString())}&end_date=${encodeURIComponent(range.end.toISOString())}`
    const resolution = windowPeriodMinutes(range)
    const aggregation = 'mean'

    const [readingsRes] = await Promise.all([
      SensorResource.getEnergySensorReadings({
        sensorId: props.sensorId,
        dateRange,
        resolution,
        aggregation,
      }),
      fetchAlarmPeriods(range),
    ])

    const raw = readingsRes?.readings
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const norm = normalizeEnergyReadingsForChart(raw as Record<string, Array<{ t?: string; v?: number }>>)
      seriesCurrentA.value = norm.current_a
      seriesCurrentB.value = norm.current_b
      seriesCurrentC.value = norm.current_c
      seriesAverage.value = norm.average
    } else {
      seriesCurrentA.value = []
      seriesCurrentB.value = []
      seriesCurrentC.value = []
      seriesAverage.value = []
    }
    savedDataZoom.value = { start: 0, end: 100 }
  } catch {
    seriesCurrentA.value = []
    seriesCurrentB.value = []
    seriesCurrentC.value = []
    seriesAverage.value = []
    savedDataZoom.value = { start: 0, end: 100 }
  } finally {
    isLoading.value = false
  }
}

const currentRange = ref<TimeRangeValue | null>(null)

function onRangeChange(range: TimeRangeValue) {
  currentRange.value = range
  fetchReadings(range)
}

/** Extrai start/end do dataZoom da instância atual para preservar ao trocar de container */
function captureDataZoomState() {
  if (!chartInstance) return
  const opt = chartInstance.getOption() as { dataZoom?: Array<{ type?: string; start?: number; end?: number }> } | undefined
  const dz = opt?.dataZoom
  if (Array.isArray(dz) && dz.length > 0) {
    const withRange = dz.find(
      (d) => typeof (d as { start?: number; end?: number }).start === 'number' && typeof (d as { start?: number; end?: number }).end === 'number',
    ) as { start: number; end: number } | undefined
    if (withRange) {
      savedDataZoom.value = { start: withRange.start, end: withRange.end }
    }
  }
}

function openFullscreen() {
  captureDataZoomState()
  const zoomToRestore = { ...savedDataZoom.value }
  isFullscreen.value = true
  nextTick(() => {
    nextTick(() => {
      savedDataZoom.value = { ...zoomToRestore }
      initChart()
      nextTick(() => chartInstance?.resize())
    })
  })
}

function closeFullscreen() {
  captureDataZoomState()
  isFullscreen.value = false
  nextTick(() => {
    initChart()
    nextTick(() => chartInstance?.resize())
  })
}

function getActiveChartContainer(): HTMLDivElement | null {
  return isFullscreen.value ? chartContainerFullscreen.value : chartContainer.value
}

function initChart() {
  const container = getActiveChartContainer()
  if (!container) return
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(container)
  updateChart()
}

function updateChart() {
  if (!chartInstance) return

  const option = buildCurrentChartOption({
    seriesCurrentA: seriesCurrentA.value,
    seriesCurrentB: seriesCurrentB.value,
    seriesCurrentC: seriesCurrentC.value,
    seriesAverage: seriesAverage.value,
    hoveredMarkArea: buildHoveredAlarmMarkArea(alarmPeriods.value, hoveredAlarmIndex.value),
    dataZoom: savedDataZoom.value,
    sliderBottom: 28,
    showMoveHandle: true,
  })

  chartInstance.setOption(option)
}

watch(
  [seriesCurrentA, seriesCurrentB, seriesCurrentC, seriesAverage, alarmPeriods, hoveredAlarmIndex],
  () => {
    nextTick(() => updateChart())
  },
  { deep: true },
)

watch(selectedAlarmType, () => {
  if (currentRange.value) fetchAlarmPeriods(currentRange.value).then(() => nextTick(() => updateChart()))
})

watch(isFullscreen, () => {
  nextTick(() => chartInstance?.resize())
})

const handleResize = () => chartInstance?.resize()

onMounted(() => {
  nextTick(() => initChart())
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
